/**
 * Theme management composable.
 *
 * Reads the user's preferred theme from `authStore.preferences.theme`
 * ('light' | 'dark' | 'system') and high contrast from
 * `authStore.preferences.high_contrast`, and sets the design system's
 * classes on `<html>`: `dark` or `light`, plus `high-contrast`. Dark is
 * the default. Falls back to localStorage so the correct theme is
 * rendered before the profile request completes (avoids a flash of
 * wrong theme on page load).
 *
 * Usage in layouts:
 *   const { isDark, setTheme, toggleTheme } = useTheme()
 *
 * Usage in Settings.vue (immediate visual feedback):
 *   setTheme('dark')          // applies instantly + saves to DB
 *   setHighContrast(true)
 */

import { ref, computed, watch, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'

type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'printhq_theme'
const CONTRAST_KEY = 'printhq_high_contrast'

// Module-level state so every consumer shares the same reactive value.
const _preference = ref<ThemePreference>(
  (localStorage.getItem(STORAGE_KEY) as ThemePreference) || 'dark'
)
const _highContrast = ref(localStorage.getItem(CONTRAST_KEY) === 'true')
const _systemDark = ref(
  window.matchMedia('(prefers-color-scheme: dark)').matches
)

// Derive the effective dark boolean.
const isDark = computed(() =>
  _preference.value === 'system' ? _systemDark.value : _preference.value === 'dark'
)

/** Apply the theme classes on <html>. */
function applyTheme() {
  const root = document.documentElement
  root.classList.toggle('dark', isDark.value)
  root.classList.toggle('light', !isDark.value)
  root.classList.toggle('high-contrast', _highContrast.value)
}

// Keep the DOM in sync whenever either setting changes.
watch([isDark, _highContrast], applyTheme, { immediate: true })

// Listen for OS-level theme changes.
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
const onMediaChange = (e: MediaQueryListEvent) => {
  _systemDark.value = e.matches
}
mediaQuery.addEventListener('change', onMediaChange)

export function useTheme() {
  const authStore = useAuthStore()

  /**
   * Initialise from the user's saved preference.  Call this once
   * after the profile has been fetched (e.g. in AppLayout onMounted).
   */
  function initFromProfile() {
    const pref = authStore.preferences?.theme as ThemePreference | undefined
    if (pref) {
      _preference.value = pref
      localStorage.setItem(STORAGE_KEY, pref)
    }
    const hc = authStore.preferences?.high_contrast
    if (typeof hc === 'boolean') {
      _highContrast.value = hc
      localStorage.setItem(CONTRAST_KEY, String(hc))
    }
  }

  /**
   * Change theme.  Applies visually immediately, persists to
   * localStorage, and saves to the user's profile in the DB.
   */
  async function setTheme(theme: ThemePreference) {
    _preference.value = theme
    localStorage.setItem(STORAGE_KEY, theme)
    // Fire-and-forget DB save – we don't need to await.
    authStore.updatePreferences({ theme })
  }

  /** Turn high contrast on or off for either theme. */
  async function setHighContrast(on: boolean) {
    _highContrast.value = on
    localStorage.setItem(CONTRAST_KEY, String(on))
    authStore.updatePreferences({ high_contrast: on })
  }

  /** Convenience toggle (cycles: current effective → opposite). */
  function toggleTheme() {
    setTheme(isDark.value ? 'light' : 'dark')
  }

  // If the profile loads *after* the composable is first called,
  // pick up the saved preference reactively.
  const stopWatch = watch(
    () => [authStore.preferences?.theme, authStore.preferences?.high_contrast] as const,
    ([pref, hc]) => {
      if (pref && pref !== _preference.value) {
        _preference.value = pref as ThemePreference
        localStorage.setItem(STORAGE_KEY, pref)
      }
      if (typeof hc === 'boolean' && hc !== _highContrast.value) {
        _highContrast.value = hc
        localStorage.setItem(CONTRAST_KEY, String(hc))
      }
    }
  )

  onUnmounted(() => {
    stopWatch()
  })

  return {
    /** Reactive boolean – `true` when dark mode is active. */
    isDark,
    /** The raw preference ('light' | 'dark' | 'system'). */
    preference: _preference,
    /** Reactive boolean – `true` when high contrast is on. */
    highContrast: _highContrast,
    initFromProfile,
    setTheme,
    setHighContrast,
    toggleTheme,
  }
}
